// @greenlight-spec: 16-api-conventions.md

/**
 * Accumulating cursor pagination for views whose derived state (counts,
 * filters, deep-link targets) spans the whole result set rather than one page.
 * The caller owns both the cursor state and the `useQuery` call, so request
 * args stay endpoint-shaped; this hook owns the page accumulator.
 *
 *   const [cursor, setCursor] = React.useState<string | undefined>(undefined);
 *   const query = api.thing.list.useQuery(['thing', cursor ?? null], …);
 *   const pages = useCursorPages<Item>({
 *     query, cursor, onCursorChange: setCursor, getKey: (i) => i.id,
 *   });
 *
 * Views that page rather than accumulate (`/apps`, `/audit`, `/feedback`) keep
 * their Prev/Next cursor stacks — this is the growing-set counterpart.
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface PageQueryResult {
  data?: { status: number; body: unknown } | undefined;
  isError: boolean;
  isLoading: boolean;
  refetch?: () => unknown;
}

interface LoadedPage<T> {
  /** The cursor that produced this page; `undefined` for the first. */
  cursor: string | undefined;
  items: T[];
  nextCursor: string | null;
}

export interface CursorPagesResult<T> {
  /** Every item loaded so far, in page order, deduplicated by `getKey`. */
  items: T[];
  /** The first page failed — the caller shows its full-view error state. */
  hasError: boolean;
  isLoadingFirstPage: boolean;
  isLoadingMore: boolean;
  /** A later page failed; `items` still holds the pages before it. */
  loadMoreFailed: boolean;
  hasMore: boolean;
  loadMore: () => void;
  retry: () => void;
  /** Drop every loaded page and re-walk from the first. */
  reset: () => void;
}

// Bounds `autoLoadWhile` so a predicate that never goes false (or data that
// keeps shifting under the keyset) can't walk the endpoint forever.
const MAX_AUTO_LOADS = 20;

export function useCursorPages<T>({
  query,
  cursor,
  onCursorChange,
  getKey,
  autoLoadWhile,
  invalidateKey,
}: {
  query: PageQueryResult;
  cursor: string | undefined;
  onCursorChange: (cursor: string | undefined) => void;
  getKey: (item: T) => string;
  /**
   * Keep fetching while this returns true — for callers that must reach a
   * specific row (a deep-link target), or that need the complete set rather
   * than whatever has loaded, instead of waiting for the viewport.
   */
  autoLoadWhile?: (items: T[]) => boolean;
  /**
   * Query-key prefix the accumulated pages belong to. Given it, a mutation that
   * invalidates this list re-walks from the first page. This is not optional
   * bookkeeping: accumulated items are copied out of the query cache, so
   * invalidation alone can only refresh the one page still mounted — every
   * earlier page would keep serving pre-mutation rows, and a newly created row
   * (which sorts onto the first page) would not appear at all.
   */
  invalidateKey?: readonly unknown[];
}): CursorPagesResult<T> {
  const [pages, setPages] = React.useState<LoadedPage<T>[]>([]);
  const autoLoadsRef = React.useRef(0);

  const { data, isError, isLoading } = query;
  const resolvedOk = !isError && data?.status === 200;
  const failed = isError || (data !== undefined && data.status !== 200);

  React.useEffect(() => {
    if (!resolvedOk) return;
    const body = data.body as { items: T[]; next_cursor: string | null };
    setPages((current) => {
      const at = current.findIndex((p) => p.cursor === cursor);
      const page: LoadedPage<T> = { cursor, items: body.items, nextCursor: body.next_cursor };
      if (at === -1) return [...current, page];
      const existing = current[at];
      // Content, not identity and not just ids: a rebuilt object with the same
      // rows is a no-op, but an in-place status change (running → applied) is not.
      const sameRows = (a: T[], b: T[]): boolean => JSON.stringify(a) === JSON.stringify(b);
      if (
        existing !== undefined &&
        at === current.length - 1 &&
        existing.nextCursor === page.nextCursor &&
        sameRows(existing.items, page.items)
      ) {
        return current;
      }
      // A refetch of page N invalidates the pages after it: the keyset they were
      // read from has shifted, so they are dropped and re-walked.
      return [...current.slice(0, at), page];
    });
  }, [resolvedOk, data, cursor]);

  const items = React.useMemo(() => {
    // Keyset paging can repeat a row when the underlying set shifts between page
    // reads; the accumulated list must still hold each row once.
    const seen = new Set<string>();
    const flat: T[] = [];
    for (const page of pages) {
      for (const item of page.items) {
        const key = getKey(item);
        if (seen.has(key)) continue;
        seen.add(key);
        flat.push(item);
      }
    }
    return flat;
  }, [pages, getKey]);

  const nextCursor = pages[pages.length - 1]?.nextCursor ?? null;
  const hasMore = nextCursor !== null;

  const loadMore = React.useCallback(() => {
    if (nextCursor === null || isLoading) return;
    onCursorChange(nextCursor);
  }, [nextCursor, isLoading, onCursorChange]);

  const retry = React.useCallback(() => {
    query.refetch?.();
  }, [query]);

  const reset = React.useCallback(() => {
    autoLoadsRef.current = 0;
    setPages([]);
    onCursorChange(undefined);
  }, [onCursorChange]);

  const queryClient = useQueryClient();
  const serializedInvalidateKey =
    invalidateKey === undefined ? undefined : JSON.stringify(invalidateKey);
  React.useEffect(() => {
    if (serializedInvalidateKey === undefined) return;
    const prefix = JSON.parse(serializedInvalidateKey) as readonly unknown[];
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'invalidate') return;
      const key = event.query.queryKey;
      const matchesPrefix =
        key.length >= prefix.length && prefix.every((part, i) => part === key[i]);
      if (matchesPrefix) reset();
    });
  }, [queryClient, serializedInvalidateKey, reset]);

  const wantsMore = autoLoadWhile !== undefined && pages.length > 0 && autoLoadWhile(items);
  React.useEffect(() => {
    if (!wantsMore || !hasMore || isLoading || failed) return;
    if (autoLoadsRef.current >= MAX_AUTO_LOADS) return;
    autoLoadsRef.current += 1;
    loadMore();
  }, [wantsMore, hasMore, isLoading, failed, loadMore]);

  return {
    items,
    hasError: failed && pages.length === 0,
    isLoadingFirstPage: isLoading && pages.length === 0,
    isLoadingMore: isLoading && pages.length > 0,
    loadMoreFailed: failed && pages.length > 0,
    hasMore,
    loadMore,
    retry,
    reset,
  };
}
